import { defineComponent, ref, nextTick, watch, reactive } from 'vue';
import { ElMessageBox, ElMessage } from 'element-plus';
import {
  createApproveInfo,
  retrieveBeforeApproveInfo,
} from '@/api/project-service';
import type { IManageCreateReq } from '@/api/project-service/type.d';
import { cloneDeep } from 'lodash-es';
import { formatTime } from '@toy/utils';
import {
  BUSINESS_TYPE_LIST,
  DEPARTMENT_TYPE_LIST,
  ENVIRONMENT_TYPE_LIST,
  IS_DELETE_LIST,
} from '@/constant/project-service';
import Uploader from '@/components/uploader/index';
import checkFile from '@/components/uploader/packages/utils/check-file';
import { IFileData } from '@/components/uploader/packages/types';
import { UserItem } from '@/api/auth-manage/user/type.d';
import { fuzzyQueryUser } from '@/api/auth-manage/user/index';
import { useFuzzy } from '@/hooks/use-fuzzy';
import { useAccountStore } from '@/store/account/index';
import { UploadFilled } from '@element-plus/icons-vue';
import { getUserInfo } from '@/api/account';
import { ICompanyInfoItem } from '@/api/account/type';
import { batchDictValues } from '@/api/dict';

export default defineComponent({
  components: {
    Uploader,
  },
  emits: ['update:visible', 'success'],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
    },
  },
  setup(props, { emit }) {
    const form = ref<IManageCreateReq>({
      serviceId: '',
      firstHeadOfBackend: '',
      firstHeadOfBackendId: 0,
      firstHeadOfBackendCode: '',
      firstHeadOfBackendPhone: '',
      firstUser: '',
      secondHeadOfBackend: '',
      secondHeadOfBackendId: 0,
      secondHeadOfBackendCode: '',
      secondHeadOfBackendPhone: '',
      secondUser: '',
      creatorPhone: '',
      isPublicService: '',
      businessType: '',
      department: '',
      groupName: '',
      serviceName: '',
      jarName: '',
      serviceExplain: '',
      applyReason: '',
      databaseName: '',
      projectRepositoryUrl: '',
      gatewayRouting: '',
      demandResources: '',
      environment: '',
      environmentList: [],
      exampleNum: 1,
      finishTime: '',
      remark: '',
      enclosureList: [],
    });

    const departmentList = ref<ICompanyInfoItem['orgList']>([]);

    const refForm = ref();
    const enclosureList = ref<IFileData[]>([]);
    const dictMap = reactive({
      businessList: [] as { value: string; }[],
      environmentList: [] as { value: string; }[],
    });
    // 获取字典
    const getDict = async () => {
      const res = await batchDictValues(['business', 'environment']);
      (res.data || []).forEach((it) => {
        dictMap[`${it.dictCode}List` as 'businessList' | 'environmentList'] = it.dictValues || [];
      });
    };
    getDict();

    watch(() => props.visible, async () => {
      if (props.visible) {
        enclosureList.value = [] as IFileData[];
      }
      // 获取全部信息
      if (props.id && props.visible) {
        const { data } = await retrieveBeforeApproveInfo(props.id);
        if (!data) return;
        form.value = data;
        enclosureList.value = [] as IFileData[];
        form.value.firstUser = data.firstHeadOfBackend
          ? `${data.firstHeadOfBackend}(${data.firstHeadOfBackendCode})`
          : '';
        form.value.secondUser = data.secondHeadOfBackend
          ? `${data.secondHeadOfBackend}(${data.secondHeadOfBackendCode})`
          : '';
        form.value.finishTime = formatTime(data.finishTime, 'YYYY-MM-DD HH:mm:ss');
        form.value.exampleNum = Number(data.exampleNum);
        form.value.environmentList = form.value.environment.split(',');
        if (!data.enclosureList?.length) return;
        enclosureList.value = data.enclosureList?.map((item: any) => {
          return {
            url: item.fileUrl,
            name: item.name,
          };
        });
      }
    });

    const handleOpenDialog = async () => {
      try {
        await nextTick();
        refForm.value.resetFields();
      } catch (e) {
        console.log(e);
      }
    };
    const handleCloseDialog = () => {
      refForm.value.resetFields();
      refForm.value.clearValidate();
      form.value.enclosureList = [];
      enclosureList.value = [] as IFileData[];
      // form.value.finishTime = '';
      console.log(enclosureList.value);
      emit('update:visible', false);
    };

    const handleSave = async () => {
      try {
        await nextTick();
        await refForm.value.validate();
        const params = cloneDeep(form.value);
        const { account } = useAccountStore();

        // const environment = (params.environment.split() || []) as string[];
        // params.firstHeadOfBackend = params.firstUser;
        // params.secondHeadOfBackend = params.secondUser;
        params.creatorPhone = account?.phone ?? '';

        params.environment = params.environmentList.join(',');

        params.finishTime = formatTime(params.finishTime);

        params.enclosureList = enclosureList.value.map((item) => {
          return {
            name: item.name || '',
            fileUrl: item.url,
          };
        });

        await createApproveInfo(params);

        ElMessage.success('提交成功！');
        handleCloseDialog();
        emit('success');
      } catch (err) {
        console.log(err);
      }
    };

    const onRemove = async (index: number) => {
      await ElMessageBox.confirm('是否确认删除该文件?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      });
      enclosureList.value.splice(index, 1);
      ElMessage.success('操作成功');
    };

    const {
      fuzzyLoading,
      fuzzyResponse,
      fuzzyRemoteMethod,
    } = useFuzzy<any, UserItem[]>({
      params: { keyword: '' },
      keywordKey: 'keyword',
      API: fuzzyQueryUser,
      beforeRequest(param, query) {
        return query;
      },
      beforeResponse(res) {
        return res.data?.map((user: UserItem) => {
          const { name: label, userId: value } = user;
          return { ...user, label, value };
        });
      },
    });

    const handleChangeUser = async (userInfo: UserItem) => {
      form.value.firstUser = `${userInfo.name}(${userInfo.bbCode})`;
      form.value.firstHeadOfBackend = userInfo.name;
      form.value.firstHeadOfBackendId = Number(userInfo.userId);
      form.value.firstHeadOfBackendCode = userInfo.bbCode ?? '';
      form.value.firstHeadOfBackendPhone = userInfo.mobilePhone ?? '';
      if (userInfo.name === undefined || userInfo.bbCode === undefined) {
        form.value.firstUser = '';
      }
      const res = await getUserInfo({
        operatorUserId: userInfo.userId,
      });
      departmentList.value = res.data.companyInfoList[0]?.orgList || [];
      const firstOrg = departmentList.value[0]?.orgName || '';
      // 部门下拉列表
      form.value.department = firstOrg;
      form.value.groupName = firstOrg;
    };
    // 切换部门
    const handleChangeDepartment = (value: string) => {
      form.value.groupName = value;
    };
    const handleChangeAlternativeUser = (userInfo: UserItem) => {
      form.value.secondUser = `${userInfo.name}(${userInfo.bbCode})`;
      form.value.secondHeadOfBackend = userInfo.name;
      form.value.secondHeadOfBackendId = Number(userInfo.userId);
      form.value.secondHeadOfBackendCode = userInfo.bbCode ?? '';
      form.value.secondHeadOfBackendPhone = userInfo.mobilePhone ?? '';
      if (userInfo.name === undefined || userInfo.bbCode === undefined) {
        form.value.secondUser = '';
      }
    };

    return {
      handleCloseDialog,
      handleOpenDialog,
      form,
      refForm,
      handleSave,
      rules: {
        firstUser: [
          {
            required: true,
            message: '后端服务负责人不能为空',
          },
        ],
        isPublicService: [
          { required: true, message: '是否公共服务不能为空' },
        ],
        businessType: [
          {
            required: true,
            message: '业务线不能为空',
          },
        ],
        department: [
          {
            required: true,
            message: '部门不能为空',
          },
        ],
        groupName: [
          {
            required: true,
            message: '所属小组不能为空',
          },
        ],
        serviceName: [
          {
            required: true,
            message: '服务名称不能为空',
          },
        ],
        jarName: [
          {
            required: true,
            message: 'Jar包名称不能为空',
          },
        ],
        serviceExplain: [
          {
            required: true,
            message: '服务描述不能为空',
          },
        ],
        applyReason: [
          {
            required: true,
            message: '申请原因不能为空',
          },
        ],
        projectRepositoryUrl: [
          { required: true, message: '项目代码仓库地址不能为空' },
          { type: 'url', message: '请输入正确的地址', trigger: 'blur' },
        ],
        environmentList: [
          {
            required: true,
            message: '运行环境不能为空',
          },
        ],
        exampleNum: [
          {
            required: true,
            message: '部署实例个数不能为空',
          },
        ],
        finishTime: [
          {
            required: true,
            message: '期望完成时间不能为空',
          },
        ],
      },
      BUSINESS_TYPE_LIST,
      DEPARTMENT_TYPE_LIST,
      ENVIRONMENT_TYPE_LIST,
      onRemove,
      enclosureList,
      fuzzyLoading,
      fuzzyResponse,
      fuzzyRemoteMethod,
      handleChangeUser,
      handleChangeAlternativeUser,
      UploadFilled,
      departmentList,
      handleChangeDepartment,
      dictMap,
    };
  },
  render() {
    return (
      <el-dialog
        title='供应链项目资源申请'
        modelValue={this.visible}
        onClose={this.handleCloseDialog}
        onOpen={this.handleOpenDialog}
        center
        width="734px"
        top="5vh"
        close-on-press-escape={false}
        close-on-click-modal={false}
        custom-class="el-dialog-inner-scroll"
        v-slots={{
          footer: () => (
            <div class="align-center">
              <el-button size="default" onClick={this.handleCloseDialog}>
                取 消
              </el-button>
              <el-button type="primary" size="default" onClick={this.handleSave}>
                提交
              </el-button>
            </div>
          ),
        }}
      >
        <el-form
          model={this.form}
          label-width="160px"
          size="default"
          rules={this.rules}
          ref="refForm"
        >
          <el-form-item label="后端服务负责人：" prop="firstUser">
            {/* <el-input v-model={this.form.applicantName} placeholder="请输入" /> */}
            <el-select
              v-model={this.form.firstUser}
              placeholder="请输入"
              remote
              filterable
              clearable
              loading={this.fuzzyLoading}
              remote-method={this.fuzzyRemoteMethod}
              style="width: 100%"
              onChange={this.handleChangeUser}
            >
              {this.fuzzyResponse.map(item => (
                <el-option value={item} label={`${item.label}(${item.bbCode})`} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="后端服务备选负责人：" prop="secondUser">
            {/* <el-input v-model={this.form.applicantName} placeholder="请输入" /> */}
            <el-select
              v-model={this.form.secondUser}
              placeholder="请输入"
              remote
              filterable
              clearable
              loading={this.fuzzyLoading}
              remote-method={this.fuzzyRemoteMethod}
              style="width: 100%"
              onChange={this.handleChangeAlternativeUser}
            >
              {this.fuzzyResponse.map(item => (
                <el-option value={item} label={`${item.label}(${item.bbCode})`} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="是否公共服务：" prop="isPublicService">
            <el-select
              v-model={this.form.isPublicService}
              style="width: 100%;"
            >
              {IS_DELETE_LIST.map(item => (
                <el-option value={item.value} label={item.label} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="业务线：" prop="businessType">
            <el-select
              v-model={this.form.businessType}
              style="width: 100%;"
            >
              {this.dictMap.businessList.map(item => (
                <el-option value={item.value} label={item.value} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="部门：" prop="department">
            <el-select
              v-model={this.form.department}
              style="width: 100%;"
              onChange={this.handleChangeDepartment}
            >
              {this.departmentList.map(item => (
                <el-option value={item.orgName} label={item.orgName} key={item.orgId} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="所属小组：" prop="groupName">
            <el-input v-model={this.form.groupName} placeholder="请输入具体部门下属小组" />
          </el-form-item>
          <el-form-item label="服务名称：" prop="serviceName">
            <el-input v-model={this.form.serviceName} placeholder="请输入" />
          </el-form-item>
          <el-form-item label="Jar包名称：" prop="jarName">
            <el-input v-model={this.form.jarName} placeholder="请输入" />
          </el-form-item>
          <el-form-item label="服务描述：" prop="serviceExplain">
            <el-input
              v-model={this.form.serviceExplain}
              type="textarea"
              placeholder="如统一用户管理"
            />
          </el-form-item>
          <el-form-item label="申请原因：" prop="applyReason">
            <el-input
              v-model={this.form.applyReason}
              type="textarea"
              placeholder="请说明申请原因；如增加XXX的功能"
            />
          </el-form-item>
          <el-form-item label="网关路由：" prop="gatewayRouting">
            <el-input v-model={this.form.gatewayRouting} placeholder="选填，http服务必填" />
          </el-form-item>
          <el-form-item label="数据库名称：" prop="databaseName">
            <el-input v-model={this.form.databaseName} placeholder="没有可不填" />
          </el-form-item>
          <el-form-item label="中间件补充：" prop="demandResources">
            <el-input
              maxlength={100}
              v-model={this.form.demandResources}
              type="textarea"
              placeholder="选填，说明所需版本、集群方式，最好能提供预估的并发、tps的"
            />
          </el-form-item>
          <el-form-item label="运行环境：" prop="environmentList">
            <el-select
              v-model={this.form.environmentList}
              style="width: 100%;"
              multiple
            >
              {this.dictMap.environmentList.map(item => (
                <el-option value={item.value} label={item.value} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="期望完成时间：" prop="finishTime">
            <el-date-picker
              style="width: 100%;"
              v-model={this.form.finishTime}
              format="YYYY-MM-DD HH:mm:ss"
              type="datetime"
              placeholder="请选择"
            >
            </el-date-picker>
          </el-form-item>
          <el-form-item label="部署实例个数：" prop="exampleNum">
            <el-input-number v-model={this.form.exampleNum} min={1} />
          </el-form-item>
          <el-form-item label="项目代码仓库地址：" prop="projectRepositoryUrl" placeholder="请输入" >
            <el-input v-model={this.form.projectRepositoryUrl} />
          </el-form-item>
          <el-form-item label="备注：" prop="remark">
            <el-input
              maxlength={200}
              v-model={this.form.remark}
              type="textarea"
              placeholder="请输入" />
          </el-form-item>
          <el-form-item label="附件：" prop="enclosureList">
            <Uploader
              v-model={this.enclosureList}
              onRemove={this.onRemove}
              // onSuccess={this.onSuccess}
              onExceed={() => ElMessage.warning('附件资料每项不应超过5份文件')}
              useWrapper={false}
              paste={false}
              listPosition="after"
              listType="text"
              size="mini"
              sizeLimit="20"
              limit={5}
              beforeUpload={(file: IFileData) => checkFile(
                file,
                '.rar, .zip, .doc, .docx, .pdf, .xlsx, xls, .png, .jpg, .jpeg',
              )}
              accept=".rar, .zip, .doc, .docx, .pdf, .xlsx, xls, .png, .jpg, .jpeg"
              tips="支持格式：.rar, .zip, .doc, .docx, .pdf, .xlsx, xls, .png, .jpg, .jpeg，单个文件不能超过20MB"
            >
              {
                this.enclosureList.length < 5 && (
                  <el-button
                    plain
                    round
                    size="mini"
                    icon={UploadFilled}
                  >
                    添加附件
                  </el-button>
                )
              }
            </Uploader>
          </el-form-item>
        </el-form>
      </el-dialog>
    );
  },
});
